// Dev-only instrumentation for GraphQL requests. `logResponse` records
// per-operation stats from response headers into sessionStorage, where
// `components/Devtools.tsx` renders them.

// This file sits in every GraphQL fetcher's import chain. Lightweight shared
// primitives (the copy button below) are fine — they're already in the common
// chunk — but keep heavy modules (echarts, DataTable, ...) out, or importing
// them here would drag all of it into that eagerly-loaded graph.
import { toast } from "@/components/ui/cn/toast"
import { CopyToClipboard } from "@/components/ui/CopyToClipboard"

export interface OpLog {
  op: string
  timestamp: number
  queryCount: number
  queryMillis: number
}

export const GRAPHQL_LOG_KEY = "graphql-log"

// Row-color thresholds for the devtools panel. Crossing a threshold also
// raises a toast, so it's visible even with the panel closed.
export const QUERY_COUNT_WARN_THRESHOLD = 10
export const QUERY_COUNT_ERROR_THRESHOLD = 50

export type QueryToastTier = "warn" | "error"

// A ready-to-paste Claude Code prompt that runs the `fix-graphql-n-plus-one`
// skill against the offending operation. The query-count toast copies this so a
// dev can drop it straight into a Claude Code session to investigate the N+1.
export const n1FixPrompt = (op: string) => `/fix-graphql-n-plus-one ${op}`

const QUERY_TOAST_DISABLED_KEYS: Record<QueryToastTier, string> = {
  warn: "graphql-toast-warn-disabled",
  error: "graphql-toast-error-disabled",
}

// Keyed per operation *and* tier: a refetch loop updates one toast instead of
// stacking, and a later warning for an operation can't overwrite the error
// toast it already raised.
const queryToastId = (tier: QueryToastTier, op: string) =>
  `sql-query-count-${tier}-${op}`

// Sonner only keeps the three most recent toasts of a stack on screen, and both
// tiers share the top-right one. Warnings fire far more often than errors, so a
// burst of them used to push a red toast out of view before it could be read;
// while any error toast is up, warnings are dropped instead. Nothing is lost —
// every response still lands in the devtools panel and the console.
function hasLiveErrorToast(): boolean {
  return toast
    .getToasts()
    .some((t) => String(t.id).startsWith(queryToastId("error", "")))
}

// Both tiers toast by default; each can be turned off from the AdminBar
// popover ("SQL query toasts"). Kept in localStorage rather than the request
// log's sessionStorage: silencing a tier is a standing preference, so it should
// outlive the tab it was set in.
export function isQueryToastEnabled(tier: QueryToastTier): boolean {
  return localStorage.getItem(QUERY_TOAST_DISABLED_KEYS[tier]) === null
}

export function setQueryToastEnabled(tier: QueryToastTier, enabled: boolean) {
  if (enabled) {
    localStorage.removeItem(QUERY_TOAST_DISABLED_KEYS[tier])
  } else {
    localStorage.setItem(QUERY_TOAST_DISABLED_KEYS[tier], "1")
  }
}

export function logResponse(response: Response) {
  const op: OpLog = {
    op: response.headers.get("x-graphql-operation") || "",
    queryCount: parseInt(response.headers.get("x-sql-query-count") || ""),
    queryMillis: parseInt(response.headers.get("x-sql-query-millis") || ""),
    timestamp: +new Date(),
  }
  console.info(op)
  const log: OpLog[] = JSON.parse(sessionStorage.getItem(GRAPHQL_LOG_KEY) || "[]")
  log.push(op)
  sessionStorage.setItem(GRAPHQL_LOG_KEY, JSON.stringify(log.slice(-50)))
  const tier =
    op.queryCount > QUERY_COUNT_ERROR_THRESHOLD
      ? "error"
      : op.queryCount > QUERY_COUNT_WARN_THRESHOLD
        ? "warn"
        : null
  if (tier === null || !isQueryToastEnabled(tier)) return
  if (tier === "warn" && hasLiveErrorToast()) return
  // Escalating past the red threshold clears the operation's stale yellow
  // toast. Only in this direction — a warning never clears an error.
  if (tier === "error") toast.dismiss(queryToastId("warn", op.op))
  const raise = tier === "error" ? toast.error : toast.caution
  raise(`${op.op} made ${op.queryCount} SQL queries`, {
    id: queryToastId(tier, op.op),
    position: "top-right",
    // Rendered as-is in the toast's action slot: a copy button that puts a
    // ready-to-paste `fix-graphql-n-plus-one` prompt for this op on the clipboard.
    action: (
      <CopyToClipboard text={n1FixPrompt(op.op)}>Copy fix prompt</CopyToClipboard>
    ),
  })
}
